import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTable, useGlobalFilter } from 'react-table';
import html2pdf from 'html2pdf.js';
import { Pencil, Plus, Download, Search, Trash2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './farmdata.css';

const FactoryDataTable = () => {
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedFactoryId, setSelectedFactoryId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newFactory, setNewFactory] = useState({ id: '', fname: '', farea: '', fno: '' });
  const [editFactory, setEditFactory] = useState({ id: '', fname: '', farea: '', fno: '' });

  const tableRef = useRef();

  useEffect(() => {
    fetch('https://kmtea.onrender.com/factoryinfo')
      .then(res => res.json())
      .then(setData)
      .catch(console.error);
  }, []);

  const columns = useMemo(() => [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Name', accessor: 'fname' },
    { Header: 'Location', accessor: 'farea' },
    { Header: 'Phone Number', accessor: 'fno' },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableSortBy: true,
      Cell: ({ row }) => {
        const location = row.original.farea?.toLowerCase();
        let iconColor = '#555';
        if (location === 'ooty') iconColor = '#3f51b5';
        else if (location === 'kamandu') iconColor = '#4caf50';
        else if (location === 'kunnur') iconColor = '#e91e63';
        else iconColor = '#ff9800';

        return (
          <div className="action-icons">
            <button
              style={{ border: 'none', background: 'transparent', padding: '0 6px', cursor: 'pointer' }}
              onClick={() => {
                setEditFactory(row.original);
                setIsEditOpen(true);
              }}
            >
              <Pencil size={18} color={iconColor} />
            </button>
            <button
              style={{ border: 'none', background: 'transparent', padding: '0 6px', cursor: 'pointer' }}
              onClick={() => {
                setSelectedFactoryId(row.original.id);
                setShowDeleteModal(true);
              }}
            >
              <Trash2 size={18} color="red" />
            </button>
          </div>
        );
      }
    }
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter: setFilter
  } = useTable({ columns, data }, useGlobalFilter);

  const handleSearch = (e) => {
    const value = e.target.value || undefined;
    setGlobalFilter(value);
    setFilter(value);
  };

  const handleDownloadPDF = () => {
    html2pdf().from(tableRef.current).save('factories.pdf');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFactory(prev => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFactory(prev => ({ ...prev, [name]: value }));
  };

  const handleAddFactory = async (e) => {
    e.preventDefault();
    const res = await fetch('https://kmtea.onrender.com/factoryadd', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newFactory)
    });
    const result = await res.json();
    if (result.success) {
      setData(prev => [...prev, result.newFactory]);
      setNewFactory({ id: '', fname: '', farea: '', fno: '' });
      setIsModalOpen(false);
      toast.success('Factory added successfully!');
    } else {
      toast.error('Failed to add factory!');
    }
  };

  const handleUpdateFactory = async (e) => {
    e.preventDefault();
    const res = await fetch(`https://kmtea.onrender.com/factory/${editFactory.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editFactory)
    });
    const result = await res.json();
    if (result.success) {
      const updated = data.map(fac =>
        fac.id === editFactory.id ? { ...fac, ...editFactory } : fac
      );
      setData(updated);
      setIsEditOpen(false);
      toast.success('Factory updated successfully!');
    } else {
      toast.error('Update failed!');
    }
  };

  const handleDeleteFactory = async () => {
    try {
      const res = await fetch(`https://kmtea.onrender.com/factory/${selectedFactoryId}`, {
        method: 'DELETE',
      });
      const result = await res.json();
      if (result.success) {
        setData(prev => prev.filter(factory => factory.id !== selectedFactoryId));
        setShowDeleteModal(false);
        setSelectedFactoryId(null);
        toast.success('Factory deleted successfully!');
      } else {
        toast.error('Failed to delete factory!');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Something went wrong!');
    }
  };

  return (
    <div className="datatable-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="header-actions">
        <h2>Factory Details</h2>
        <div className="search-filter-wrapper">
          <Search size={16} />
          <input
            type="text"
            placeholder="Quick search..."
            value={globalFilter || ''}
            onChange={handleSearch}
          />
        </div>
        <div className="action-btns">
          <button onClick={handleDownloadPDF}>
            <Download size={16} /> Download
          </button>
          <button onClick={() => setIsModalOpen(true)}>
            <Plus size={16} /> Add New
          </button>
        </div>
      </div>

      <div ref={tableRef}>
        <table {...getTableProps()} className="styled-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()} key={column.id}>
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={row.id}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()} key={cell.column.id}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>+ Add New Factory</h2>
            <form onSubmit={handleAddFactory}>
              <div className="form-group">
                <label>Factory ID</label>
                <input type="text" name="id" value={newFactory.id} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input type="text" name="fname" value={newFactory.fname} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" name="farea" value={newFactory.farea} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" name="fno" value={newFactory.fno} onChange={handleInputChange} required />
              </div>
              <button type="submit">Add</button>
              <button type="button" onClick={() => setIsModalOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>Edit Factory</h2>
            <form onSubmit={handleUpdateFactory}>
              <div className="form-group">
                <label>ID</label>
                <input type="text" name="id" value={editFactory.id} disabled />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input type="text" name="fname" value={editFactory.fname} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" name="farea" value={editFactory.farea} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" name="fno" value={editFactory.fno} onChange={handleEditChange} required />
              </div>
              <button type="submit">Update</button>
              <button type="button" onClick={() => setIsEditOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this factory?</p>
            <button onClick={handleDeleteFactory} style={{ background: 'red' }}>Yes, Delete</button>
            <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactoryDataTable;
